import { history } from 'flow/configure-store';
import ContactResource from 'flow/api/contacts';
import FlowResource from 'flow/api/flow';
import InvitationsResource from 'flow/api/invitations';
import ThirdPartyContactsResource from 'flow/api/thirdpartycontacts';
import Actions from 'flow/actions';
import {
    networkErrorDispatcher,
    httpGetErrorDispatcher,
    setSnackbar,
} from 'flow/actions/app-actions';
import { fetchExtension } from 'flow/actions/extension-actions';
import { getIdFromEntityKey } from 'flow/utils';
import { generatePath } from 'flow/utils/router-utils';
import { routes } from 'flow/types/routes';
import { getFocusedSession } from 'flow/selectors/call-selectors';

export const fetchContact = (contactKey) => {
    return (dispatch) => {
        ContactResource.getContactUsingEntityKey(
            {
                onResult(json) {
                    dispatch(receiveContact(json));
                },
                onError: httpGetErrorDispatcher(dispatch),
            },
            contactKey,
        );
    };
};

export const fetchEditableContact = (contactEntityKey) => (dispatch) =>
    new Promise((resolve, reject) => {
        ContactResource.getEditableContactUsingEntityKey(
            {
                onResult(data) {
                    dispatch(receiveEditableContact(data));
                    resolve();
                },
                onError(error) {
                    httpGetErrorDispatcher(dispatch);
                    reject(error);
                },
                onClientError: (error) => {
                    httpGetErrorDispatcher(dispatch);
                    reject(error);
                },
            },
            contactEntityKey,
        );
    });

export const updateEditableContact =
    (editableContactDTO, extensionKey, handleSuccess = () => {}) =>
    (dispatch) => {
        ContactResource.updateEditableContact(
            {
                onResult(data) {
                    dispatch(receiveEditableContact(data));
                    setTimeout(() => dispatch(fetchContact(data.key)), 300); // Filthy filthy DB-replication...
                    if (extensionKey) {
                        setTimeout(
                            () =>
                                dispatch(
                                    fetchExtension(extensionKey, 'contact'),
                                ),
                            300,
                        ); // Filthy filthy DB-replication...
                    }
                    dispatch(
                        setSnackbar({
                            i18nextMessage: 'Settings.Update.Snackbar.Success',
                        }),
                    );
                    handleSuccess();
                },
                onError() {
                    dispatch(
                        setSnackbar({
                            i18nextMessage: 'Settings.Update.Snackbar.Fail',
                        }),
                    );
                },
                onClientError() {
                    dispatch(
                        setSnackbar({
                            i18nextMessage: 'Settings.Update.Snackbar.Fail',
                        }),
                    );
                },
            },
            editableContactDTO,
        );
    };

export const updateExternalEditableContact =
    (editableContactDTO, handleSuccess = () => {}) =>
    (dispatch) => {
        ContactResource.updateEditableContact(
            {
                onResult(data) {
                    dispatch(receiveEditableContact(data));
                    setTimeout(
                        () => dispatch(fetchExternalContact(data.key)),
                        300,
                    ); // Filthy filthy DB-replication...
                    handleSuccess();
                    dispatch(
                        setSnackbar({
                            i18nextMessage: 'Settings.Update.Snackbar.Success',
                        }),
                    );
                },
                onError: httpGetErrorDispatcher(dispatch),
                onClientError() {
                    dispatch(
                        setSnackbar({
                            i18nextMessage: 'Settings.Update.Snackbar.Fail',
                        }),
                    );
                },
            },
            editableContactDTO,
        );
    };

export const fetchExternalContact = (contactKey) => {
    return (dispatch) => {
        ContactResource.getContactUsingEntityKey(
            {
                onResult(json) {
                    dispatch(receiveUpdatedContact(json));
                },
                onError: httpGetErrorDispatcher(dispatch),
            },
            contactKey,
        );
    };
};

export const updateContact = (contactDTO) => (dispatch) => {
    ContactResource.updateContact(
        {
            onResult(data) {
                dispatch(receiveUpdatedContact(data));
                dispatch(
                    setSnackbar({
                        i18nextMessage: 'Settings.Update.Snackbar.Success',
                    }),
                );
            },
            onError: httpGetErrorDispatcher(dispatch),
            onClientError() {
                dispatch(
                    setSnackbar({
                        i18nextMessage: 'Settings.Update.Snackbar.Fail',
                    }),
                );
            },
        },
        contactDTO,
    );
};

export const deleteContact = (extensionKey) => (dispatch) => {
    const flowAccountEntityKey =
        'flowaccount-' + getIdFromEntityKey(extensionKey);
    const flowAccountDTO = {
        key: flowAccountEntityKey,
    };

    FlowResource.deleteFlowUser(
        {
            onResult(/*json*/) {
                dispatch(removeContact(extensionKey));
                dispatch(
                    setSnackbar({
                        i18nextMessage: 'contact.deleted',
                    }),
                );
            },
            onError: networkErrorDispatcher(dispatch),
        },
        flowAccountDTO,
    );
};

export const inviteExternalContact = (invitationDTO) => (dispatch) => {
    InvitationsResource.addInvite(
        {
            onResult() {
                //Do nothing
            },
            onError: networkErrorDispatcher(dispatch),
        },
        invitationDTO,
    );
};

export const removeContact = (extensionKey) => {
    return {
        type: Actions.RESULT_REMOVE_CONTACT,
        key: extensionKey,
    };
};

export const receiveContact = (json) => {
    return {
        type: Actions.RESULT_CONTACT,
        data: json,
    };
};

export const receiveEditableContact = (json) => {
    return {
        type: Actions.RESULT_EDITABLE_CONTACT,
        data: json,
    };
};

export const updatedAvatarEvent = (json, extensionKey) => {
    return {
        type: Actions.RESULT_CONTACT_AVATAR_UPDATE,
        data: json,
        extensionKey: extensionKey,
    };
};

export const deleteAvatarPic = (json, extensionKey) => {
    return {
        type: Actions.RESULT_CONTACT_AVATAR_DELETE,
        data: json,
        extensionKey: extensionKey,
    };
};

export const updateAvatarImage =
    (contactKey, image, extensionKey) => (dispatch) => {
        ContactResource.updateAvatarWithData(
            {
                onResult(data) {
                    dispatch(updatedAvatarEvent(data, extensionKey));
                },
                onError: networkErrorDispatcher(dispatch),
            },
            contactKey,
            image,
        );
    };

export const deleteAvatarImage = (contactKey, extensionKey) => (dispatch) => {
    ContactResource.deleteAvatar(
        {
            onResult(data) {
                dispatch(deleteAvatarPic(data, extensionKey));
            },
            onError: networkErrorDispatcher(dispatch),
        },
        contactKey,
    );
};

export const createContact =
    (contactDTO, presentContact = false) =>
    (dispatch) => {
        ContactResource.createContact(
            {
                onResult(data) {
                    dispatch(
                        setSnackbar({
                            i18nextMessage: 'contact.created',
                        }),
                    );
                    dispatch(receiveExternalContact(data));
                    if (presentContact) {
                        history.push(
                            generatePath(
                                routes['extensions.contact.contactKey'],
                                { contactKey: data.key },
                            ),
                        );
                    }
                },
                onError: networkErrorDispatcher(dispatch),
            },
            contactDTO,
        );
    };

export const createContactTW =
    (contactDTO, presentContact = false) =>
    (dispatch) => {
        ContactResource.createContact(
            {
                onResult(data) {
                    dispatch(
                        setSnackbar({
                            i18nextMessage: 'contact.created',
                        }),
                    );
                    dispatch(receiveExternalContact(data));
                    if (presentContact) {
                        history.push(`/extensions/contact/${data.key}`);
                    }
                },
                onError: networkErrorDispatcher(dispatch),
            },
            contactDTO,
        );
    };

export const fetchContacts = (contactType) => (dispatch) => {
    ContactResource.listContactsByType(
        {
            onResult(res) {
                dispatch(receiveFetchContacts(res));
            },
            onError: httpGetErrorDispatcher(dispatch),
        },
        contactType,
    );
};

export const fetchPersonalAndSharedContacts = () => (dispatch) => {
    dispatch(fetchContacts('personal'));
    dispatch(fetchContacts('shared'));
};

export const fetchSocialIntegrationContacts = () => (dispatch) => {
    ThirdPartyContactsResource.listThirdPartyContacts({
        onResult(res) {
            dispatch(receiveFetchSocialIntegrationContacts(res));
        },
        onError: httpGetErrorDispatcher(dispatch),
    });
};

export const lookupNumber = (number) => (dispatch, getState) => {
    const session = getFocusedSession(getState());
    let logId = null;
    if (session && session.data && session.data.logId) {
        logId = session.data.logId;
    }
    return new Promise((resolve, reject) => {
        ContactResource.lookupContactsUsingNumber(
            {
                onResult(res) {
                    dispatch(receiveNumberLookupInfo(res, number));
                    resolve(res);
                },
                onError: (error) => {
                    httpGetErrorDispatcher(dispatch)(error);
                    reject(error);
                },
            },
            number,
            logId,
        );
    });
};

const receiveNumberLookupInfo = (response, number) => ({
    type: Actions.RESULT_NUMBER_LOOKUP,
    number,
    data: response,
});

const receiveFetchContacts = (contactList) => ({
    type: Actions.RESULT_CONTACTS,
    data: contactList,
});

const receiveFetchSocialIntegrationContacts = (contactList) => ({
    type: Actions.RESULT_SOCIAL_INTEGRATION_CONTACTS,
    data: contactList,
});

export const removeExternalContact = (contactKey) => (dispatch) => {
    ContactResource.deleteContactUsingEntityKey(
        {
            onResult(/**/) {
                dispatch(
                    setSnackbar({
                        i18nextMessage: 'contact.deleted',
                    }),
                );
                dispatch(receiveRemoveContact(contactKey));
            },
            onError: networkErrorDispatcher(dispatch),
        },
        contactKey,
    );
};

const receiveRemoveContact = (contactKey) => ({
    type: Actions.REMOVE_EXTERNAL_CONTACT,
    key: contactKey,
});

const receiveExternalContact = (contact) => ({
    type: Actions.RESULT_NEW_EXTERNAL_CONTACT,
    data: contact,
});

const receiveUpdatedContact = (contact) => ({
    type: Actions.RESULT_UPDATED_CONTACT,
    data: contact,
});
